import { Delete as DeleteIcon } from "@mui/icons-material";
import {
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
} from "@mui/material";
import { makeNewId } from "common/ids";
import Disabled from "components/common/Disabled";
import PrettyNumberTextField from "components/common/PrettyNumberTextField";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as programActions from "store/actions/input/program/programActions";
import * as programSelectors from "store/selectors/input/program/programSelectors";

const isBlankFee = (fee) => !fee?.label && fee?.amount == null;

const normalizeWithTrailingBlank = (list) => {
  const nonBlanks = list.filter((f) => !isBlankFee(f));
  const existingTrailing =
    list.length > 0 && isBlankFee(list[list.length - 1])
      ? list[list.length - 1]
      : { id: makeNewId(), label: "", amount: null };
  return [...nonBlanks, existingTrailing];
};

const colLabelSx = { px: 0.5, py: 0.5, fontWeight: 600, fontSize: "0.75rem" };
const cellSx = { px: 0.5, py: 0.5, borderBottom: "none" };
const formatTotal = (v) =>
  v != null ? v.toLocaleString(undefined, { maximumFractionDigits: 0 }) : "—";

const FeesTable = ({ layerPremium }) => {
  const dispatch = useDispatch();
  const program = useSelector(programSelectors.selectResolvedProgram);
  const rawFees = program?.sidecarFees ?? null;
  const fees = rawFees ?? [];
  const surplusLinesTax = program?.sidecarSurplusLinesTax ?? null;

  useEffect(() => {
    const current = rawFees ?? [];
    if (current.length === 0 || !isBlankFee(current[current.length - 1])) {
      dispatch(
        programActions.updateProgram({
          sidecarFees: normalizeWithTrailingBlank(current),
        })
      );
    }
  }, [dispatch, rawFees]);

  const updateFee = (id, patch) => {
    const updated = fees.map((f) => (f.id === id ? { ...f, ...patch } : f));
    dispatch(
      programActions.updateProgram({
        sidecarFees: normalizeWithTrailingBlank(updated),
      })
    );
  };
  const deleteFee = (id) => {
    dispatch(
      programActions.updateProgram({
        sidecarFees: normalizeWithTrailingBlank(
          fees.filter((f) => f.id !== id)
        ),
      })
    );
  };

  const feesTotal = fees
    .filter((f) => !isBlankFee(f))
    .reduce((sum, f) => (f?.amount != null ? sum + f.amount : sum), 0);
  const grandTotal =
    layerPremium != null
      ? layerPremium + feesTotal + (surplusLinesTax ?? 0)
      : null;

  return (
    <Table size={"small"}>
      <TableHead>
        <TableRow>
          <TableCell sx={colLabelSx}>{"Fees (fully earned)"}</TableCell>
          <TableCell sx={{ ...colLabelSx, width: 140 }} align={"right"}>
            {"Amount"}
          </TableCell>
          <TableCell sx={{ ...colLabelSx, width: 36 }} />
        </TableRow>
      </TableHead>
      <TableBody>
        {fees.map((fee) => (
          <TableRow key={fee.id}>
            <TableCell sx={cellSx}>
              <TextField
                variant={"filled"}
                size={"small"}
                hiddenLabel
                fullWidth
                value={fee.label ?? ""}
                onChange={(e) => updateFee(fee.id, { label: e.target.value })}
              />
            </TableCell>
            <TableCell sx={cellSx} align={"right"}>
              <PrettyNumberTextField
                variant={"filled"}
                size={"small"}
                hiddenLabel
                fullWidth
                inputProps={{ style: { textAlign: "right" } }}
                value={fee.amount ?? null}
                onChangeNumber={(v) => updateFee(fee.id, { amount: v })}
              />
            </TableCell>
            <TableCell sx={cellSx}>
              {!isBlankFee(fee) && (
                <Disabled ifReadOnly>
                  <Tooltip title={"Remove fee"}>
                    <span>
                      <IconButton
                        size={"small"}
                        onClick={() => deleteFee(fee.id)}
                      >
                        <DeleteIcon fontSize={"inherit"} />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Disabled>
              )}
            </TableCell>
          </TableRow>
        ))}
        <TableRow>
          <TableCell sx={cellSx}>
            <TextField
              variant={"filled"}
              size={"small"}
              hiddenLabel
              fullWidth
              disabled
              value={"Surplus Lines Tax"}
            />
          </TableCell>
          <TableCell sx={cellSx} align={"right"}>
            <PrettyNumberTextField
              variant={"filled"}
              size={"small"}
              hiddenLabel
              fullWidth
              inputProps={{ style: { textAlign: "right" } }}
              value={surplusLinesTax}
              onChangeNumber={(v) =>
                dispatch(
                  programActions.updateProgram({ sidecarSurplusLinesTax: v })
                )
              }
            />
          </TableCell>
          <TableCell sx={cellSx} />
        </TableRow>
        <TableRow>
          <TableCell sx={{ ...cellSx, fontWeight: 600 }}>{"Total"}</TableCell>
          <TableCell
            sx={{ ...cellSx, fontWeight: 600, pr: "1rem" }}
            align={"right"}
          >
            {formatTotal(grandTotal)}
          </TableCell>
          <TableCell sx={cellSx} />
        </TableRow>
      </TableBody>
    </Table>
  );
};

export default FeesTable;
